import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Link } from "react-router";
import { NoteIcon } from "../../../../../assets";
import {
  useGetSpecificNotesQuery,
  useLegislationArticleByIdQuery,
} from "../../../../services";
import { processDocumentText } from "../../../../utils";
import { Toggle } from "../_toggle";
import "./index.scss";

interface NotesModalProps {
  showNoteModal: boolean;
  setShowNoteModal: (b: boolean) => void;
  noteTitle: string;
  type: "note" | "legislation";
  noteContent: string;
  articleId?: string;
}

const NotesModal = ({
  setShowNoteModal,
  showNoteModal,
  noteTitle,
  noteContent,
  type,
  articleId,
}: NotesModalProps) => {
  const [hideRevoked, setHideRevoked] = useState(false);

  const [internalType, setInternalType] = useState<"note" | "legislation">(
    type
  );
  const [internalTitle, setInternalTitle] = useState(noteTitle);
  const [internalContent, setInternalContent] = useState(noteContent);
  const [internalArticleId, setInternalArticleId] = useState(articleId || "");
  const [notas, setNotas] = useState<string[]>([]);

  const contentRef = useRef<HTMLDivElement>(null);

  const { data, isPending, isError, error } = useLegislationArticleByIdQuery(
    internalArticleId,
    internalType === "legislation" && showNoteModal && !!internalArticleId
  );

  const legislationId = internalArticleId.replace(/art\d+$/i, "");

  const { data: fetchedNotes, isPending: notesPending } =
    useGetSpecificNotesQuery(
      legislationId,
      notas,
      internalType === "note" && notas.length > 0
    );

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const onClick = (event: any) => {
      const target = event.target;

      if (target.classList?.contains("nota-super")) {
        let next = target.nextElementSibling;

        while (next) {
          if (next.className?.includes("nota-texto")) {
            next.classList.toggle("oculto");
            break;
          }
          next = next.nextElementSibling;
        }
        return;
      }

      if (target.hasAttribute?.("data-nota-link")) {
        event.preventDefault();
        const noteKey = target.getAttribute("data-nota-link");
        if (!noteKey) return;

        const legislationId =
          data?.documento?.id || internalArticleId?.replace(/art\d+$/i, "");

        if (!legislationId) return;

        setInternalType("note");
        setInternalTitle(`Nota ${noteKey.slice(4, 99999)}`);

        setNotas([noteKey.replace("nota", "")]);

        return;
      }

      const link = event.target.closest("a");
      if (link?.hasAttribute("data-full-link")) {
        return;
      }
      if (link) {
        const href = link.getAttribute("href") || "";
        const match =
          href.match(/(art\d+[A-Za-z]*)/i) || href.match(/(art\d+)/i);

        if (match) {
          event.preventDefault();
          const articlePart = match[1];

          let cleaned = href.replace(/^\/?legislacao\//i, "");
          cleaned = cleaned.split("#")[0];

          if (!cleaned.toLowerCase().endsWith(articlePart.toLowerCase())) {
            cleaned = cleaned.replace(/art\d+[A-Za-z]*/i, "") + articlePart;
          }

          setInternalType("legislation");
          setInternalArticleId(cleaned);

          return;
        }
      }
    };

    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [data, internalContent]);

  useEffect(() => {
    if (!fetchedNotes?.notas?.length) return;

    const nota = fetchedNotes.notas[0];
    if (nota) {
      setInternalContent(
        processDocumentText({
          htmlText: nota.text || nota.texto || "Nota não possui texto",
          hideRevoked,
        })
      );
    }
  }, [fetchedNotes]);

  useEffect(() => {
    if (showNoteModal) {
      setInternalType(type);
      setInternalTitle(noteTitle);
      setInternalContent(noteContent);
      setInternalArticleId(articleId || "");
    }
  }, [showNoteModal, type, noteTitle, noteContent, articleId]);

  useEffect(() => {
    if (!showNoteModal) {
      setInternalType(type);
      setInternalTitle(noteTitle);
      setInternalContent(noteContent);
      setInternalArticleId(articleId || "");
      setNotas([]);
      setHideRevoked(false);
    }
  }, [showNoteModal]);

  return (
    <Modal
      backdrop="static"
      contentClassName="h-100 d-flex flex-column align-items-stretch justify-content-between gap-4"
      dialogClassName="h-100"
      className="d-flex flex-row align-items-stretch justify-content-end"
      show={showNoteModal}
      onHide={() => setShowNoteModal(false)}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {internalType === "note"
            ? internalTitle
            : data?.documento?.titulo || data?.documento?.nome}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ overflowY: "auto" }}>
        <div ref={contentRef} id="modal">
          {internalType === "legislation" ? (
            isPending ? (
              <Container>
                <Row>
                  <Col>
                    <div className="content-loading">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="mt-3">Buscando artigo....</p>
                    </div>
                  </Col>
                </Row>
              </Container>
            ) : isError ? (
              <Container>
                <Row>
                  <Col>
                    <div className="text-center mt-5">
                      <h4 className="text-danger">
                        Ops! Parece que não foi possível buscar artigo
                      </h4>
                      <p>
                        {(error as AxiosError<{ message: string }>)?.response
                          ?.data?.message ||
                          "Não foi possível encontrar o conteúdo do artigo"}
                      </p>
                    </div>
                  </Col>
                </Row>
              </Container>
            ) : (
              <>
                <div className="modal-show-full-note-container">
                  <Link
                    className="modal-show-full-note"
                    data-full-link="true"
                    to={`/legislacao/${data?.documento.id}#${data?.artigo.ref}`}
                  >
                    <NoteIcon /> Ver norma completa
                  </Link>
                </div>

                <Toggle
                  label="Texto compilado"
                  checked={hideRevoked}
                  onChange={() => setHideRevoked(!hideRevoked)}
                />

                <div
                  className="note-content"
                  dangerouslySetInnerHTML={{
                    __html: processDocumentText({
                      htmlText: data?.artigo.texto || "",
                      hideRevoked,
                    }),
                  }}
                />
              </>
            )
          ) : notesPending && notas.length > 0 ? (
            <Container>
              <Row>
                <Col>
                  <div className="content-loading">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="mt-3">Buscando conteúdo....</p>
                  </div>
                </Col>
              </Row>
            </Container>
          ) : (
            <div
              className="note-content"
              dangerouslySetInnerHTML={{ __html: internalContent }}
            />
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default NotesModal;
